/**
 * 1.8.0 rights management and genealogy: the data hooks (docs/dev/rights-1.8.0-build.md). Step 0 wrote them against
 * the routes (stream A builds them; until then genealogy and the reports answer 501); stream B owns this file now and
 * fixes a hook when its route turns out different.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GenealogyPageDTO, RightsCountsDTO, RightsFilter, RightsSettings, RightsSettingsDTO } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

/** The workspace's rights settings (everyone may read them: `enabled` and the policy decide what the dialogs ask). */
export function useRightsSettings(enabled = true) {
  return useQuery({ queryKey: queryKeys.rightsSettings(), queryFn: () => api<RightsSettingsDTO>('/api/v1/settings/rights'), enabled, staleTime: 60_000 });
}

/** Administration → Rights (admin). */
export function useSaveRightsSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RightsSettings) => api<RightsSettingsDTO>('/api/v1/settings/rights', { method: 'PUT', json: body }),
    onSuccess: (data) => qc.setQueryData(queryKeys.rightsSettings(), data),
  });
}

/** What an asset is made from and what it is used in (the Related tab's Made from and Used in, the Rights tab). */
export function useGenealogy(assetId: string | undefined, cursor?: string | null) {
  return useQuery({
    queryKey: queryKeys.genealogy(assetId, cursor ?? null),
    queryFn: () => api<GenealogyPageDTO>(`/api/v1/assets/${assetId}/genealogy${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
    enabled: Boolean(assetId),
    retry: false,
  });
}

/** The rights reports' download URLs (Download rights report (CSV)). */
export const rightsReportUrl = {
  sequence: (sequenceId: string) => `/api/v1/sequences/${sequenceId}/rights-report.csv`,
  asset: (assetId: string) => `/api/v1/assets/${assetId}/rights-report.csv`,
};

/**
 * Administration → Rights: how many assets each Library filter finds (GET /api/v1/settings/rights/counts, admin; the
 * server counts with the Library's own filters, so a count and the filter it links to agree). Only while rights
 * management is on.
 */
export function useRightsCounts(keys: readonly RightsFilter[], enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.rightsCounts(), keys.join(',')],
    queryFn: async () => {
      const r = await api<RightsCountsDTO>('/api/v1/settings/rights/counts');
      return Object.fromEntries(keys.map((k) => [k, r.counts[k] ?? null])) as Partial<Record<RightsFilter, number | null>>;
    },
    enabled,
    staleTime: 30_000,
  });
}
